import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Loader2, Save, AtSign, Mail, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, roleBadgeClass } from '../lib/api';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { UserAvatar } from '../components/UserAvatar';

export default function Settings() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, setUser } = useAuth();
  const [username, setUsername] = useState(user?.username || user?.name || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const role = user?.role || 'player';

  const errText = (err, fallback) => {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'object' && detail?.code === 'USERNAME_TAKEN') return t('usernameTaken');
    if (typeof detail === 'object' && detail?.code === 'INVALID_USERNAME') return t('usernameHint');
    if (typeof detail === 'object' && detail?.message) return detail.message;
    return typeof detail === 'string' ? detail : fallback;
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/auth/profile', { username: username.trim() });
      setUser(data);
      toast.success(t('profileUpdated'));
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/auth/profile/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUser((prev) => ({ ...prev, picture: data.picture }));
      toast.success(t('avatarUpdated'));
    } catch (err) {
      toast.error(errText(err, 'Error'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const dirty = username.trim() !== (user?.username || user?.name || '');

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="settings-page">
      <div className="flex items-center justify-between pt-1">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors" data-testid="settings-back-button">
          <ArrowLeft size={15} /> {t('backToDashboard')}
        </button>
      </div>

      <div className="pt-1">
        <h1 className="font-display font-bold text-2xl tracking-tight text-white">{t('profileSettings')}</h1>
        <p className="text-sm text-white/55 mt-1">{t('settingsSub')}</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-6 space-y-6">
        {/* avatar */}
        <div className="flex items-center gap-5">
          <div className="relative" data-testid="settings-avatar">
            <UserAvatar src={user?.picture} alt={user?.name} size={88} rounded={24} />
            {uploading && (
              <span className="absolute inset-0 rounded-[24px] bg-black/55 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-white" />
              </span>
            )}
          </div>
          <div className="space-y-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="settings-change-photo"
              className="ss-btn-glass h-10 px-4 text-xs font-medium flex items-center gap-2"
            >
              <Camera size={14} /> {t('changePhoto')}
            </button>
            <p className="font-mono text-[10px] text-white/35">JPG / PNG / WebP · ≤ 5MB</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid="settings-avatar-input"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
            />
          </div>
        </div>

        {/* username */}
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45 flex items-center gap-1.5"><AtSign size={11} /> {t('usernameLabel')}</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={20}
            data-testid="settings-username-input"
            className="w-full h-12 rounded-[14px] bg-white/[0.05] border border-white/10 px-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none"
          />
          <p className="font-mono text-[10px] text-white/35">{t('usernameHint')}</p>
        </div>

        {/* email (read only) */}
        <div className="space-y-1.5">
          <label className="font-mono text-[11px] uppercase tracking-wider text-white/45 flex items-center gap-1.5"><Mail size={11} /> {t('emailLabel')}</label>
          <div className="w-full h-12 rounded-[14px] bg-white/[0.03] border border-white/[0.07] px-3.5 text-sm text-white/55 flex items-center justify-between" data-testid="settings-email">
            <span className="truncate">{user?.email}</span>
            <span className={`shrink-0 border rounded-[10px] px-1.5 py-0.5 text-[10px] font-mono uppercase ${roleBadgeClass(role)}`} data-testid="settings-role-badge">
              {t('roleBadge')[role]}
            </span>
          </div>
          {user?.is_email_verified && (
            <p className="font-mono text-[10px] text-white/35 flex items-center gap-1"><ShieldCheck size={10} /> verified</p>
          )}
        </div>

        <button
          onClick={saveProfile}
          disabled={saving || !dirty || username.trim().length < 3}
          data-testid="settings-save-button"
          className="ss-btn-primary w-full h-12 font-display font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t('saveChanges')}
        </button>
      </motion.div>
    </div>
  );
}
